import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Button, Card, Form, Modal, Alert, Carousel } from 'react-bootstrap';
import './Landing.css';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';
import { apiUrl, authUrl, assetUrl } from '../utils/apiUrl';


function getPostUrl(post) {
  if (!post?.slug) return '#';
  const lang = post.language === 'en' ? 'en' : 'es';
  const base = lang === 'en' ? '/news' : '/noticias';
  const paths = { en: { movie: 'movies', series: 'series', music: 'music' }, es: { movie: 'peliculas', series: 'series', music: 'musica' } };
  const categoryPath = paths[lang][post.category] || paths[lang].movie;
  return `${base}/${categoryPath}/${post.slug}`;
}

// En local solo mostramos entrada por email (sin Google)
const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const Landing = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [showEmailAuth, setShowEmailAuth] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [latestPosts, setLatestPosts] = useState([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(authUrl('/auth/me'), { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled && data) setUser(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingNews(true);
    const lang = language === 'en' ? 'en' : 'es';
    fetch(apiUrl(`/api/blog/posts?language=${lang}&limit=6`), { credentials: 'include' })
      .then((r) => r.ok ? r.json() : { posts: [] })
      .then((data) => {
        if (!cancelled && Array.isArray(data.posts)) setLatestPosts(data.posts);
      })
      .catch(() => { if (!cancelled) setLatestPosts([]); })
      .finally(() => { if (!cancelled) setLoadingNews(false); });
    return () => { cancelled = true; };
  }, [language]);

  // Detectar errores en la URL (viene del redirect de Google)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    if (errorParam) {
      const errorMessages = {
        google_not_configured: t('landing.errorGoogleNotConfigured', language),
        google_auth_failed: t('landing.errorGoogleAuthFailed', language),
        no_user: t('landing.errorNoUser', language),
        callback_error: t('landing.errorCallback', language),
        session_error: t('landing.errorSession', language)
      };
      setError(errorMessages[errorParam] || t('landing.errorSignIn', language));
      // Limpiar la URL
      window.history.replaceState({}, '', '/');
    }
  }, [language]);

  const handleLoginGoogle = () => {
    window.location.href = authUrl('/auth/google');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const url = isRegister ? authUrl('/auth/register') : authUrl('/auth/login');
      const body = isRegister ? { email, password, name: name || email.split('@')[0] } : { email, password };
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || t('landing.errorSignInGeneric', language));
        setLoading(false);
        return;
      }
      setShowEmailAuth(false);
      navigate('/app', { replace: true });
    } catch (err) {
      setError(t('landing.errorConnection', language));
      setLoading(false);
    }
  };

  const openEmailLogin = () => {
    setIsRegister(false);
    setEmail('');
    setPassword('');
    setName('');
    setError('');
    setShowEmailAuth(true);
  };

  const openEmailRegister = () => {
    setIsRegister(true);
    setEmail('');
    setPassword('');
    setName('');
    setError('');
    setShowEmailAuth(true);
  };

  return (
    <div className="landing">
      {/* Selector de idioma y foto de perfil (si está logueado) en la esquina superior derecha */}
      <div className="position-fixed top-0 end-0 p-3 d-flex align-items-center gap-2" style={{ zIndex: 1050 }}>
        <LanguageSelector />
        {user && (
          <Link to="/app" className="d-flex align-items-center" title={user.name || user.email}>
            {user.picture ? (
              <img
                src={user.picture}
                width={28}
                height={28}
                alt=""
                className="me-1 me-md-2 rounded-circle"
                style={{ objectFit: 'cover' }}
              />
            ) : (
              <span className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-1 me-md-2" style={{ width: 28, height: 28, fontSize: '0.85rem' }}>
                {(user.name || user.email || '?').charAt(0).toUpperCase()}
              </span>
            )}
          </Link>
        )}
      </div>

      <section className="landing-hero">
        <div className="hero-background"></div>
        <Container>
          <Row className="align-items-center min-vh-100 py-5">
            <Col lg={7} className="hero-content">
              <div className="hero-badge mb-3">
                <span className="badge-text">
                  {t('landing.heroBadge', language)}
                </span>
              </div>
              <h1 className="display-3 fw-bold mb-4 hero-title">
                {t('landing.heroTitle', language)} <span className="text-gradient">{t('landing.heroTitleHighlight', language)}</span>
              </h1>
              <p className="lead mb-4 hero-subtitle">
                {t('landing.heroSubtitle', language)}
              </p>
              <div className="hero-features mb-4">
                <div className="hero-feature-item">
                  <span className="feature-check">✓</span>
                  <span>{t('landing.featureRealTitles', language)}</span>
                </div>
                <div className="hero-feature-item">
                  <span className="feature-check">✓</span>
                  <span>{t('landing.featureAIPowered', language)}</span>
                </div>
                <div className="hero-feature-item">
                  <span className="feature-check">✓</span>
                  <span>{t('landing.featureFreeForever', language)}</span>
                </div>
              </div>
              <div className="d-flex flex-wrap gap-3">
                {isLocal ? (
                  <Button
                    size="lg"
                    className="btn-hero-primary px-5 py-3"
                    onClick={openEmailLogin}
                  >
                    <span className="me-2">✉️</span> {t('landing.modalSignInEmail', language)}
                  </Button>
                ) : (
                  <>
                    <Button 
                      size="lg" 
                      className="btn-hero-primary px-5 py-3" 
                      onClick={handleLoginGoogle}
                    >
                      <img 
                        src="/google_login.png" 
                        alt="Google" 
                        className="google-icon"
                        onError={(e) => {
                          e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJNMTcuNjQgOS4yMDVjMC0uNjM5LS4wNTctMS4yNzItLjE2NC0xLjg2OUg5djMuNTI3aDQuODQ0Yy0uMjA5IDEuMTI1LS44NDQgMi4wNzctMS43OTYgMi44NDV2Mi4yNTVoMi45MDhjMS42OTgtMS42OTggMi42MTctNC4yMTEgMi42MTctNy4xODh6IiBmaWxsPSIjNDI4NUY0Ii8+PHBhdGggZD0iTTkgMThjMi40MyAwIDQuNDY3LS44MDYgNS45NjEtMi4xNjNsLTIuOTA4LTIuMjU1Yy0uODA1LjUzOS0xLjgzNi44NTgtMy4wNTMuODU4LTIuMzQ5IDAtNC4zMzctMS41ODQtNS4wNDUtMy43MTFIMC45NTR2Mi4zMzJDMi40MzggMTUuOTgzIDUuNDgyIDE4IDkgMTh6IiBmaWxsPSIjMzRBODUzIi8+PHBhdGggZD0iTTMuOTU1IDEwLjg3MUwuOTU0IDguNTM5Qy4zNDggOS44OTkgMCAxMS40MzQgMCAxM2MwIDEuNTY2LjM0OCAzLjEwMS45NTQgNC40NjFsMy4wMDEtMi4zMzJjLS40MDgtMS4xMjctLjQwOC0yLjM4NSAwLTMuNTEyeiIgZmlsbD0iI0ZCQkMwNCIvPjxwYXRoIGQ9Ik05IDMuNTU4YzEuMzIxIDAgMi41MDcuNDQyIDMuNDQwIDEuMzEyTDguNDQxIDcuMDQ1QzcuNzYzIDYuNzE2IDYuODQxIDYuNDQ0IDUuODI4IDYuNDQ0IDMuNDc5IDYuNDQ0IDEuNDY3IDguMDI4Ljc1OSAxMC4xNjRsLTMuMDAxLTIuMzMyQzIuNDM4IDQuOTk3IDUuNDgyIDMuNTU4IDkgMy41NTh6IiBmaWxsPSIjRUE0MzM1Ii8+PC9nPjwvc3ZnPg==";
                        }}
                      />
                      {t('landing.ctaButton', language)}
                    </Button>
                    <div className="trust-indicators">
                      <span className="trust-text">
                        {t('landing.trustText', language)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </Col>
            <Col lg={5} className="hero-visual d-none d-lg-block">
              <div className="hero-cards">
                <div className="hero-card hero-card-1">
                  <div className="card-icon">🎬</div>
                  <div className="card-label">{t('landing.cardMovies', language)}</div>
                </div>
                <div className="hero-card hero-card-2">
                  <div className="card-icon">📺</div>
                  <div className="card-label">{t('landing.cardSeries', language)}</div>
                </div>
                <div className="hero-card hero-card-3">
                  <div className="card-icon">🎵</div>
                  <div className="card-label">{t('landing.cardMusic', language)}</div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {latestPosts.length > 0 && (
        <section className="landing-news py-4 py-md-5 bg-light">
          <Container>
            <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-md-between gap-2 mb-4">
              <h2 className="text-center text-md-start fw-bold mb-0">{t('landing.latestNewsTitle', language)}</h2>
              <Link to="/all-news" className="btn btn-outline-primary btn-sm">
                {t('landing.allNewsLink', language)}
              </Link>
            </div>
            <Carousel indicators controls className="landing-news-carousel">
              {latestPosts.map((post) => (
                <Carousel.Item key={post.id}>
                  <Link to={getPostUrl(post)} className="text-decoration-none text-dark d-block">
                    <div className="row align-items-center g-0 bg-white rounded shadow-sm overflow-hidden">
                      <div className="col-md-5">
                        {post.featured_image ? (
                          <img
                            src={assetUrl(post.featured_image)}
                            alt=""
                            className="w-100 h-100 object-fit-cover"
                            style={{ minHeight: 220, maxHeight: 280, objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="bg-secondary d-flex align-items-center justify-content-center" style={{ minHeight: 220 }}>
                            <span className="display-4 text-white opacity-50">📰</span>
                          </div>
                        )}
                      </div>
                      <div className="col-md-7 p-4">
                        <h5 className="fw-bold mb-2">{post.title}</h5>
                        <p className="text-muted small mb-0 line-clamp-3">{post.excerpt || post.title}</p>
                        <span className="text-primary small mt-2 d-inline-block">{t('landing.latestNewsReadMore', language)} →</span>
                      </div>
                    </div>
                  </Link>
                </Carousel.Item>
              ))}
            </Carousel>
          </Container>
        </section>
      )}

      {!loadingNews && latestPosts.length === 0 && (
        <section className="landing-news py-4 py-md-5 bg-light">
          <Container>
            <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-md-between gap-2 mb-4">
              <h2 className="text-center text-md-start fw-bold mb-0">{t('landing.latestNewsTitle', language)}</h2>
              <Link to="/all-news" className="btn btn-outline-primary btn-sm">
                {t('landing.allNewsLink', language)}
              </Link>
            </div>
            <p className="text-center text-muted">{t('landing.latestNewsNone', language)}</p>
          </Container>
        </section>
      )}

      <section className="landing-features py-5">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="fw-bold mb-3">
              {t('landing.featuresTitle', language)}
            </h2>
            <p className="text-muted lead">
              {t('landing.featuresSubtitle', language)}
            </p>
          </div>
          <Row xs={1} md={3} className="g-4">
            <Col>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="feature-icon mb-3">🎬</div>
                  <Card.Title>
                    {t('landing.featurePersonalizedTitle', language)}
                  </Card.Title>
                  <Card.Text className="text-muted">
                    {t('landing.featurePersonalizedDesc', language)}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="feature-icon mb-3">⭐</div>
                  <Card.Title>
                    {t('landing.featureRateTitle', language)}
                  </Card.Title>
                  <Card.Text className="text-muted">
                    {t('landing.featureRateDesc', language)}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="feature-icon mb-3">📊</div>
                  <Card.Title>
                    {t('landing.featureTrackTitle', language)}
                  </Card.Title>
                  <Card.Text className="text-muted">
                    {t('landing.featureTrackDesc', language)}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="feature-icon mb-3">🔍</div>
                  <Card.Title>
                    {t('landing.featureSearchTitle', language)}
                  </Card.Title>
                  <Card.Text className="text-muted">
                    {t('landing.featureSearchDesc', language)}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="feature-icon mb-3">🎯</div>
                  <Card.Title>
                    {t('landing.featureControlTitle', language)}
                  </Card.Title>
                  <Card.Text className="text-muted">
                    {t('landing.featureControlDesc', language)}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="feature-icon mb-3">🔑</div>
                  <Card.Title>
                    {t('landing.featureOwnAITitle', language)}
                  </Card.Title>
                  <Card.Text className="text-muted">
                    {t('landing.featureOwnAIDesc', language)}
                  </Card.Text>
                  <Link to="/groq-tutorial" className="btn btn-sm btn-outline-primary mt-2">
                    {t('groqTutorial.learnMore', language)}
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="landing-how-it-works py-5">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="fw-bold mb-3">
              {t('landing.howItWorksTitle', language)}
            </h2>
            <p className="text-muted lead">
              {t('landing.howItWorksSubtitle', language)}
            </p>
          </div>
          <Row className="g-4">
            <Col md={4}>
              <div className="how-it-works-card text-center">
                <div className="step-number">1</div>
                <h4 className="mt-3 mb-3">{t('landing.step1Title', language)}</h4>
                <p className="text-muted">
                  {t('landing.step1Desc', language)}
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="how-it-works-card text-center">
                <div className="step-number">2</div>
                <h4 className="mt-3 mb-3">{t('landing.step2Title', language)}</h4>
                <p className="text-muted">
                  {t('landing.step2Desc', language)}
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="how-it-works-card text-center">
                <div className="step-number">3</div>
                <h4 className="mt-3 mb-3">{t('landing.step3Title', language)}</h4>
                <p className="text-muted">
                  {t('landing.step3Desc', language)}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="landing-cta py-5">
        <Container className="text-center">
          <h2 className="fw-bold mb-3 cta-title">
            {t('landing.ctaTitle', language)}
          </h2>
          <p className="lead mb-4 cta-subtitle">
            {t('landing.ctaSubtitle', language)}
          </p>
          <div className="d-flex flex-wrap justify-content-center gap-2">
            {isLocal ? (
              <Button size="lg" variant="outline-primary" className="px-4 py-3" onClick={openEmailLogin}>
                <span className="me-2">✉️</span> {t('landing.modalSignInEmail', language)}
              </Button>
            ) : (
              <Button 
                size="lg" 
                className="btn-hero px-4 py-3" 
                onClick={handleLoginGoogle} 
                style={{ 
                  backgroundColor: '#ffffff', 
                  borderColor: '#dadce0',
                  color: '#3c4043',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.boxShadow = 'none';
                }}
              >
                <img 
                  src="/google_login.png" 
                  alt="Google" 
                  style={{ width: 20, height: 20, marginRight: 8, verticalAlign: 'middle' }}
                  onError={(e) => {
                    e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJNMTcuNjQgOS4yMDVjMC0uNjM5LS4wNTctMS4yNzItLjE2NC0xLjg2OUg5djMuNTI3aDQuODQ0Yy0uMjA5IDEuMTI1LS44NDQgMi4wNzctMS43OTYgMi44NDV2Mi4yNTVoMi45MDhjMS42OTgtMS42OTggMi42MTctNC4yMTEgMi42MTctNy4xODh6IiBmaWxsPSIjNDI4NUY0Ii8+PHBhdGggZD0iTTkgMThjMi40MyAwIDQuNDY3LS44MDYgNS45NjEtMi4xNjNsLTIuOTA4LTIuMjU1Yy0uODA1LjUzOS0xLjgzNi44NTgtMy4wNTMuODU4LTIuMzQ5IDAtNC4zMzctMS41ODQtNS4wNDUtMy43MTFIMC45NTR2Mi4zMzJDMi40MzggMTUuOTgzIDUuNDgyIDE4IDkgMTh6IiBmaWxsPSIjMzRBODUzIi8+PHBhdGggZD0iTTMuOTU1IDEwLjg3MUwuOTU0IDguNTM5Qy4zNDggOS44OTkgMCAxMS40MzQgMCAxM2MwIDEuNTY2LjM0OCAzLjEwMS45NTQgNC40NjFsMy4wMDEtMi4zMzJjLS40MDgtMS4xMjctLjQwOC0yLjM4NSAwLTMuNTEyeiIgZmlsbD0iI0ZCQkMwNCIvPjxwYXRoIGQ9Ik05IDMuNTU4YzEuMzIxIDAgMi41MDcuNDQyIDMuNDQwIDEuMzEyTDguNDQxIDcuMDQ1QzcuNzYzIDYuNzE2IDYuODQxIDYuNDQ0IDUuODI4IDYuNDQ0IDMuNDc5IDYuNDQ0IDEuNDY3IDguMDI4Ljc1OSAxMC4xNjRsLTMuMDAxLTIuMzMyQzIuNDM4IDQuOTk3IDUuNDgyIDMuNTU4IDkgMy41NTh6IiBmaWxsPSIjRUE0MzM1Ii8+PC9nPjwvc3ZnPg==";
                  }}
                />
                {t('landing.ctaButtonSecondary', language)}
              </Button>
            )}
          </div>
        </Container>
      </section>

      <footer className="landing-footer py-4 text-center">
        <Container>
          <p className="mb-2" style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
            {t('landing.footerText', language)}
          </p>
          <p className="mb-0" style={{ color: '#6b7280', fontSize: '0.85rem' }}>
            {t('landing.footerCopyright', language)}
          </p>
        </Container>
      </footer>

      <Modal show={showEmailAuth} onHide={() => setShowEmailAuth(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isRegister 
              ? t('landing.modalCreateAccount', language)
              : t('landing.modalSignIn', language)
            }
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          <Form onSubmit={handleEmailSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t('landing.modalEmailLabel', language)}</Form.Label>
              <Form.Control
                type="email"
                placeholder={t('landing.modalEmailPlaceholder', language)}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>
            {isRegister && (
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('landing.modalNameLabel', language)}
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('landing.modalNamePlaceholder', language)}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>
                {t('landing.modalPasswordLabel', language)}
              </Form.Label>
              <Form.Control
                type="password"
                placeholder={isRegister 
                  ? t('landing.modalPasswordPlaceholderRegister', language)
                  : t('landing.modalPasswordPlaceholder', language)
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={isRegister ? 6 : 1}
              />
            </Form.Group>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading 
                  ? '...' 
                  : isRegister 
                    ? t('landing.modalSignUp', language)
                    : t('landing.modalSignInButton', language)
                }
              </Button>
              <Button
                type="button"
                variant="link"
                className="p-0"
                onClick={() => {
                  setIsRegister(!isRegister);
                  setError('');
                }}
              >
                {isRegister 
                  ? t('landing.modalAlreadyHaveAccount', language)
                  : t('landing.modalCreateAccountLink', language)
                }
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Landing;
